import React, { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { FreshSale } from "interface/sales";
import { GrideDateFormatter } from "ui/Date";

interface FreshSalesMobileListProps {
	items: FreshSale[];
	loading: boolean;
	onLoadMore: () => void;
}

type FreshSaleGroup = {
	groupKey: string;
	todaysDate?: Date;
	totalWeight: number;
	items: FreshSale[];
};

const formatWeight = (value?: number | null) => {
	const asNumber = Number(value || 0);
	return `${asNumber.toLocaleString("no-NO", {
		maximumFractionDigits: 2,
	})} kg`;
};

const formatCurrency = (value?: number | null) => {
	const asNumber = Number(value || 0);
	return asNumber.toLocaleString("no-NO", {
		maximumFractionDigits: 2,
		style: "currency",
		currency: "NOK",
	});
};

const FreshSalesMobileList: FC<FreshSalesMobileListProps> = ({
	items,
	loading,
	onLoadMore,
}) => {
	const { t } = useTranslation();

	const groupedItems = useMemo<FreshSaleGroup[]>(() => {
		const groups = new Map<string, FreshSaleGroup>();

		items.forEach((item) => {
			const groupKey = item.group1 || `${item.fishingGearText || "-"} - ${item.packageTypeText || "-"}`;
			const existing = groups.get(groupKey);

			if (!existing) {
				groups.set(groupKey, {
					groupKey,
					todaysDate: item.todaysDate,
					totalWeight: Number(item.netLiveWeightKg || 0),
					items: [item],
				});
				return;
			}

			existing.items.push(item);
			existing.totalWeight += Number(item.netLiveWeightKg || 0);
			if (!existing.todaysDate && item.todaysDate) {
				existing.todaysDate = item.todaysDate;
			}
		});

		return Array.from(groups.values());
	}, [items]);

	const empty = useMemo(() => !loading && groupedItems.length === 0, [groupedItems.length, loading]);

	useEffect(() => {
		const handleWindowScroll = () => {
			const remaining =
				document.documentElement.scrollHeight -
				window.innerHeight -
				window.scrollY;

			if (remaining < 200) {
				onLoadMore();
			}
		};

		window.addEventListener("scroll", handleWindowScroll, { passive: true });
		handleWindowScroll();

		return () => {
			window.removeEventListener("scroll", handleWindowScroll);
		};
	}, [onLoadMore, items.length, loading]);

	return (
		<div className="fresh-sales-list-mobile">
			{empty && <div className="fresh-sales-list-mobile-empty">{t("NoRows")}</div>}

			{groupedItems.map((group, groupIndex) => (
				<div
					key={`${group.groupKey}-${groupIndex}`}
					className="fresh-sales-list-card"
				>
					<div className="fresh-sales-list-card-header">
						<div className="fresh-sales-list-card-group">{group.groupKey}</div>
						<div className="fresh-sales-list-card-date">
							{group.todaysDate ? (
								<GrideDateFormatter
									date={group.todaysDate}
									formatType="dd.MM.yyyy"
									noTd={true}
								/>
							) : (
								"-"
							)}
						</div>
					</div>

					<div className="fresh-sales-list-card-body">
						{group.items.map((item, itemIndex) => (
							<div
								key={`${group.groupKey}-${item.productText || "noproduct"}-${itemIndex}`}
								className="fresh-sales-list-row"
							>
								<div className="fresh-sales-list-row-main">
									<div className="fresh-sales-list-row-title">{item.productText || "-"}</div>
								</div>
								<div className="fresh-sales-list-row-metrics">
									<div>
										<span>{t("Weight")}</span>
										<strong>{formatWeight(item.netLiveWeightKg)}</strong>
									</div>
									<div>
										<span>{t("HighestPrice")}</span>
										<strong>{formatCurrency(item.maxPrice)}</strong>
									</div>
									<div>
										<span>{t("AveragePrice")}</span>
										<strong>{formatCurrency(item.averagePrice)}</strong>
									</div>
								</div>
							</div>
						))}
					</div>

					<div className="fresh-sales-list-card-footer">
						<div>
							<span>{t("Rows")}</span>
							<strong>{group.items.length}</strong>
						</div>
						<div>
							<span>{t("TotalWeight")}</span>
							<strong>{formatWeight(group.totalWeight)}</strong>
						</div>
					</div>
				</div>
			))}

			{loading && <div className="fresh-sales-list-mobile-loading">{t("Loading")}</div>}
		</div>
	);
};

export default FreshSalesMobileList;